import { Divider, Flex, Skeleton, Stack, Text } from '@tonic-one/react';
import { WarningMinorIcon } from '@tonic-one/react-icons';
import { t } from 'i18next';

interface AnalysisResultsProps {
  isLoading: boolean;
  analysisResult: string;
  summary: string;
  items: string[];
}

export const AnalysisResults = ({
  isLoading,
  analysisResult,
  summary,
  items,
}: AnalysisResultsProps) => {
  if (isLoading) {
    return (
      <Flex px="4x" pb="4x" columnGap="6x">
        <Stack width={180} gap="4x">
          <Skeleton height="5x" width="100px" animation="wave" />
          <Skeleton height="5x" width="80px" animation="wave" />
        </Stack>
        <Divider orientation="vertical" />
        <Stack width={540} gap="4x">
          <Skeleton height="5x" width="80px" animation="wave" />
          <Skeleton height="10x" width="100%" animation="wave" />
        </Stack>
        <Divider orientation="vertical" />
        <Stack flex="1 1 auto" gap="4x">
          <Skeleton height="5x" width="180px" animation="wave" />
          <Stack gap="2x">
            <Skeleton height="5x" width="100%" animation="wave" />
            <Skeleton height="5x" width="100%" animation="wave" />
            <Skeleton height="5x" width="90%" animation="wave" />
          </Stack>
        </Stack>
      </Flex>
    );
  }

  const getResultColor = (result: string) => {
    const lowerResult = result.toLowerCase();
    if (lowerResult === 'not a risk' || lowerResult === 'normal') {
      return '#04C45A';
    }
    return '#F24C4F';
  };

  return (
    <Flex px="4x" pb="4x" columnGap="6x">
      <Stack width={180} gap="4x">
        <Text size="md" fontWeight={500}>
          {t('operations.euq.analysis.result')}
        </Text>
        <Text size="md" color={getResultColor(analysisResult)}>
          {analysisResult.charAt(0).toUpperCase() +
            analysisResult.slice(1).toLowerCase()}
        </Text>
      </Stack>
      <Divider orientation="vertical" />
      <Stack width={540} gap="4x">
        <Text size="md" fontWeight={500}>
          {t('operations.euq.summary.title')}
        </Text>
        <Text>{summary}</Text>
      </Stack>
      <Divider orientation="vertical" />
      <Stack flex="1 1 auto" gap="4x">
        <Text size="md" fontWeight={500}>
          {analysisResult?.toLowerCase() === 'not a risk'
            ? t('operations.euq.analysis.confidenceItems')
            : t('operations.euq.analysis.suspiciousIndicators')}
        </Text>
        <Stack gap="2x">
          {items.map((indicator: string, index: number) => (
            <Flex alignItems="center" key={index}>
              <WarningMinorIcon size="4x" mr="2x" color="yellow:50" mb="auto" />
              <Text size="sm">{indicator}</Text>
            </Flex>
          ))}
        </Stack>
      </Stack>
    </Flex>
  );
};
